import { createMiddleware, createStart } from "@tanstack/react-start";
import { LEGACY_REDIRECTS } from "@modules/shared/redirects/legacyRedirects";

const PUBLIC_ORIGIN = process.env.PUBLIC_ORIGIN ?? "https://postolarvarga.hr";

/**
 * Permanent (301) redirects: www → apex, and old-site addresses Google still
 * has → their current page. Firebase's own domain redirect answers 302, which
 * search engines treat as temporary, so the www domain must be served by this
 * backend in Firebase (not set to "redirect").
 */
const permanentRedirectMiddleware = createMiddleware().server(
  async ({ request, next }) => {
    const url = new URL(request.url);
    const host = request.headers.get("x-forwarded-host") ?? url.host;
    const path = url.pathname.replace(/\/+$/, "").toLowerCase();
    const legacyTarget = LEGACY_REDIRECTS[path];

    if (host.startsWith("www.") || legacyTarget) {
      // Built from the fixed public origin, never from the request's host or
      // a path like "//other.site", so it can't point anywhere else.
      const target = new URL(PUBLIC_ORIGIN);
      if (legacyTarget) {
        target.pathname = legacyTarget;
      } else {
        target.pathname = "/" + url.pathname.replace(/^[/\\]+/, "");
        target.search = url.search;
      }
      throw new Response(null, {
        status: 301,
        headers: {
          Location: target.toString(),
          // Only the path-based redirects may be cached: the www one depends
          // on a request header, and caching it could loop the apex address.
          "Cache-Control": legacyTarget
            ? "public, max-age=86400"
            : "private, no-store",
        },
      });
    }
    return next();
  },
);

/**
 * Cache monitoring middleware — logs render timing and cache-related headers.
 * On Firebase App Hosting, Cloud CDN adds `Age` and cache status headers.
 * Locally, this adds `x-rendered-at` to every SSR response for debugging.
 */
const cacheMonitoringMiddleware = createMiddleware().server(
  async ({ next }) => {
    const start = performance.now();
    const result = await next();

    const elapsed = (performance.now() - start).toFixed(1);
    result.response.headers.set("x-rendered-at", new Date().toISOString());
    result.response.headers.set("x-render-time", `${elapsed}ms`);

    // Log for server-side observability
    const url = result.response.url || "(ssr)";
    const cacheStatus =
      result.response.headers.get("cdn-cache-status") ||
      result.response.headers.get("x-goog-cache-status") ||
      result.response.headers.get("cf-cache-status") ||
      "ORIGIN";
    console.log(`[cache] ${url} | render=${elapsed}ms | cache=${cacheStatus}`);

    return result;
  },
);

/**
 * CSP middleware — adds Content-Security-Policy header allowing Shopify's
 * privacy-banner script, consent-tracking API, and analytics endpoints.
 * Only applied in production to avoid breaking Vite's dev server (HMR,
 * workers, inline scripts).
 */
const cspMiddleware = createMiddleware().server(async ({ next }) => {
  const result = await next();

  // Skip CSP in development — Vite uses blob: workers, inline scripts, and
  // WebSocket connections that a strict CSP would block.
  if (process.env.NODE_ENV !== "production") {
    return result;
  }

  const shopifyScriptSrc = "https://cdn.shopify.com";
  const checkoutDomain = process.env.VITE_CHECKOUT_DOMAIN;
  const shopifyConnectSrc = [
    "https://cdn.shopify.com",
    "https://monorail-edge.shopifysvc.com",
    "https://*.myshopify.com",
    // Shopify consent API + checkout — must be on the storefront's root domain
    // (e.g. checkout.postolarvarga.hr) so the _tracking_consent cookie is valid.
    checkoutDomain ? `https://${checkoutDomain}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  const csp = [
    `default-src 'self'`,
    `script-src 'self' 'unsafe-inline' 'unsafe-eval' ${shopifyScriptSrc}`,
    `style-src 'self' 'unsafe-inline' ${shopifyScriptSrc}`,
    `worker-src 'self' blob:`,
    `connect-src 'self' ${shopifyConnectSrc}`,
    `img-src 'self' data: https://cdn.shopify.com https://*.shopify.com`,
    `font-src 'self' data:`,
    `frame-src 'self'`,
  ].join("; ");

  result.response.headers.set("Content-Security-Policy", csp);

  return result;
});

export const startInstance = createStart(() => {
  return {
    requestMiddleware: [
      permanentRedirectMiddleware,
      cacheMonitoringMiddleware,
      cspMiddleware,
    ],
  };
});
